"use client"

import { Head } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Globe,
  User,
  Calendar,
  Eye,
  FileText,
  ArrowLeft,
  Edit,
  ExternalLink,
  BarChart3,
  Tag,
  Send,
} from "lucide-react"
import DashboardLayout from "@/layout/dashboard-layout"
import { router } from "@inertiajs/react"
import { useState } from "react"
import { toast } from "sonner"

interface Client {
  id: number
  name: string
  contact_name: string
}

interface Category {
  id: number
  name: string
}

interface Post {
  id: number
  title: string
  status: string
  views_count: number
  created_at: string
  category_name?: string
  author_name?: string
}

interface Blog {
  id: number
  name: string
  domain: string
  status: string
  created_at: string
  posts_count: number
  views_count: number
  last_post_date?: string
  active: boolean
  client: Client
  categories: Category[]
  recent_posts: Post[]
}

interface Props {
  blog: Blog
}

export default function ShowBlogPage({ blog }: Props) {
  const [isPublishing, setIsPublishing] = useState(false)

  const handlePublishPosts = async () => {
    setIsPublishing(true)
    
    try {
      const response = await fetch(`/dashboard/blogs/${blog.id}/publish_posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        toast.success('Posts publicados!', {
          description: data.message,
          duration: 4000,
        })
        // Recarregar a página para atualizar as estatísticas
        router.reload()
      } else {
        toast.error('Erro ao publicar posts', {
          description: data.message,
          duration: 4000,
        })
      }
    } catch (error) {
      toast.error('Erro ao publicar posts', {
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        duration: 4000,
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-500'
      case 'inativo':
        return 'bg-gray-500'
      case 'manutenção':
        return 'bg-yellow-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getPostStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge variant="default" className="text-xs">Publicado</Badge>
      case 'draft':
        return <Badge variant="secondary" className="text-xs">Rascunho</Badge>
      case 'scheduled':
        return <Badge variant="outline" className="text-xs">Agendado</Badge>
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>
    }
  }

  return (
    <DashboardLayout>
      <Head title={`${blog.name} - Detalhes`} />
      
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => router.visit("/dashboard/blogs")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{blog.name}</h1>
              <p className="text-muted-foreground">Detalhes e estatísticas do blog</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => router.visit(`/dashboard/blogs/${blog.id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(`https://${blog.domain}`, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Visitar Site
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações do Blog */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Informações do Blog
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nome</label>
                    <p className="font-semibold">{blog.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Domínio</label>
                    <p className="font-semibold">https://{blog.domain}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(blog.status)}`}></div>
                      <span className="font-semibold capitalize">{blog.status}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Criado em</label>
                    <p className="font-semibold">{blog.created_at}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Estatísticas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Estatísticas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <FileText className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-600">{blog.posts_count}</p>
                    <p className="text-sm text-muted-foreground">Posts</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Eye className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">{blog.views_count.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Visualizações</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <Calendar className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-purple-600">{blog.last_post_date || 'Nunca'}</p>
                    <p className="text-sm text-muted-foreground">Último Post</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Posts Recentes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Posts Recentes
                </CardTitle>
                <CardDescription>Últimas postagens do blog</CardDescription>
              </CardHeader>
              <CardContent>
                {blog.recent_posts.length > 0 ? (
                  <div className="space-y-4">
                    {blog.recent_posts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold">{post.title}</h4>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span>Por {post.author_name}</span>
                            <span>•</span>
                            <span>{post.created_at}</span>
                            {post.category_name && (
                              <>
                                <span>•</span>
                                <span>{post.category_name}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm font-semibold">{post.views_count}</p>
                            <p className="text-xs text-muted-foreground">views</p>
                          </div>
                          {getPostStatusBadge(post.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum post encontrado</p>
                    <p className="text-sm">Este blog ainda não possui postagens.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Informações do Cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nome</label>
                    <p className="font-semibold">{blog.client.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Contato</label>
                    <p className="font-semibold">{blog.client.contact_name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Categorias */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Categorias
                </CardTitle>
              </CardHeader>
              <CardContent>
                {blog.categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {blog.categories.map((category) => (
                      <Badge key={category.id} variant="outline">
                        {category.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhuma categoria associada</p>
                )}
              </CardContent>
            </Card>

            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => router.visit(`/dashboard/posts?blog_id=${blog.id}`)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Posts
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => router.visit(`/dashboard/posts/new?blog_id=${blog.id}`)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Nova Postagem
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => router.visit(`/dashboard/categories?blog_id=${blog.id}`)}
                >
                  <Tag className="h-4 w-4 mr-2" />
                  Gerenciar Categorias
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={handlePublishPosts}
                  disabled={isPublishing}
                >
                  <Send className={`h-4 w-4 mr-2 ${isPublishing ? 'animate-pulse' : ''}`} />
                  {isPublishing ? 'Publicando...' : 'Publicar Posts'}
                </Button>
              </CardContent>
            </Card>

            {/* Status do Blog */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${blog.active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <div>
                    <p className="font-semibold">{blog.active ? 'Ativo' : 'Inativo'}</p>
                    <p className="text-xs text-muted-foreground">
                      {blog.active ? 'Blog está sendo monitorado' : 'Blog não está sendo monitorado'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
